import { z } from "zod";
import { Action, ActionEnum } from "notifications/notification-action";

export const notificationActionLinkContractVersion = 1 as const;

export const NotificationActionLinkV1Z = z.object({
  contractVersion: z.literal(notificationActionLinkContractVersion),
  topicCategory: z.string().min(1),
  topicId: z.uuid(),
  action: z.string().min(1).nullish(),
});

export type NotificationActionLinkV1 = z.infer<typeof NotificationActionLinkV1Z>;

/**
 * Actions that belong to a topic other than a proposal, and so must not fall into the proposal
 * bundle below. Naming them is what keeps `Work.Proposal` from silently accepting every action any
 * other category ever introduces.
 */
const actionsBelongingToOtherTopics: ReadonlySet<string> = new Set([
  Action.AcceptOrReject,
  Action.BuildTeamRoster,
]);

const supportedActionsByTopic: Readonly<Record<string, ReadonlySet<string>>> = {
  "LegalEntities.LegalEntity": new Set(),
  "LegalEntities.EntityMember": new Set(),
  "LegalEntities.TeamRoster": new Set([Action.BuildTeamRoster]),
  "Marketplace.TeamInvitation": new Set(["AcceptOrReject"]),
  "Marketplace.Team": new Set(),
  "Messaging.Forum": new Set(),
  "Messaging.ForumInvitation": new Set(),
  "Users.NetworkInvitation": new Set(["AcceptOrReject"]),
  "Work.Proposal": new Set(ActionEnum.filter((action) => !actionsBelongingToOtherTopics.has(action))),
  "Work.Agreement": new Set(),
};

export function parseNotificationActionLink(value: unknown): NotificationActionLinkV1 | undefined {
  const parsed = NotificationActionLinkV1Z.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export function isKnownNotificationAction(value: string | null | undefined): value is Action {
  return value === null || value === undefined
    ? false
    : ActionEnum.includes(value as Action);
}

export function isSupportedNotificationActionLink(link: NotificationActionLinkV1): boolean {
  const supportedActions = supportedActionsByTopic[link.topicCategory];
  if (!supportedActions) return false;
  return link.action === null || link.action === undefined || supportedActions.has(link.action);
}
